<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-lg"></i>
          {{ $label("event.events", "Events") }}
          <router-link
            :to="{ name: 'NewEvent' }"
            v-tooltip="{
              content: $label('event.new_event', 'New event'),
              classes: 'custom-tooltip'
            }"
            class="btn btn-v-lightgreen-white pull-right"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
          <select
            v-model="timeframeFilter"
            id="event-timeframe-select"
            class="form-control header-select pull-right"
            style="margin-right: 15px;"
          >
            <option
              v-for="(timeframe, index) in eventTimeframes"
              :key="index"
              :value="timeframe"
              >{{ timeframe }}</option
            >
          </select>
          <select
            v-model="typeFilter"
            id="event-type-select"
            class="form-control header-select pull-right"
            style="margin-right: 15px;"
          >
            <option
              v-for="(type, index) in eventTypes"
              :key="index"
              :value="type"
              >{{ type }}</option
            >
          </select>
        </h1>
      </div>
    </div>
    <input type="hidden" id="customer-display-all" value="false" />

    <navigation-tabs></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest"
      class="mt-8"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label(
          "event.events_failed_request",
          "Request failed while loading events."
        )
      }}
    </alert-box>

    <Table
      v-show="!failedRequest"
      id="events-table"
      :loading="loading"
      :columns="[
        {
          title: 'Event',
          field: 'name',
          id: row => `event-name-${row.id}`,
          onClick: row =>
            $router.push({
              name: 'EventDetails',
              params: { event_id: row.id }
            })
        },
        {
          title: 'Type',
          id: row => `event-type-${row.id}`,
          value: row => (row.type === 'course' ? 'Course' : row.type)
        },
        {
          title: 'Date',
          id: row => `event-date-${row.id}`,
          value: row =>
            `${dateObjectToString(
              new Date(row.start_date)
            )} - ${dateObjectToString(new Date(row.end_date))}`
        },
        {
          title: 'Max. participants',
          field: 'max_participants',
          id: row => `event-max-participants-${row.id}`
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'EventDetails',
              params: { event_id: row.id }
            });
          }
        },
        {
          type: 'delete',
          id: row => `delete-event-button-${row.id}`,
          onClick: row => deleteEvent(row.id)
        }
      ]"
      :data="events"
      :meta="metaData"
      selectable
      @onRowSelect="() => {}"
      @fetchQuery="
        params => {
          fetchParams = params;
          getEvents({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewEvent' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{
                $label("event.new_event_long", "Create new event")
              }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import NavigationTabs from "@/components/Settings/Events/NavigationTabs.vue";

import swal from "sweetalert";

export default {
  name: "Events",

  components: {
    Table,
    AlertBox,
    NavigationTabs
  },

  data() {
    return {
      componentKey: 0,
      events: [],
      eventTypes: [],
      eventTimeframes: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        to: 1,
        search: ""
      },
      timeframeFilter: "all",
      typeFilter: "course",
      selectedEventId: null
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedEventId = vm.$route.params.event_id;
      if (
        vm.$route.query.event_deleted === true ||
        vm.$route.query.event_deleted === "true"
      ) {
        swal("Success!", "The event was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getEvents(() => {
        //
      });
      vm.getEventTypes();
      vm.getEventTimeframes();
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.event_deleted === true) {
      swal("Success!", "The event was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getEvents(() => {
      //
    });
    this.getEventTypes();
    this.getEventTimeframes();
    next();
  },

  mounted() {
    //
  },

  methods: {
    getEvents(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.events = [];
      const endpoint = this.$partnerEndpoint("events");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search,
            timeframe: this.timeframeFilter,
            type: this.typeFilter
          }
        })
        .then(response => {
          this.events = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getEventTypes() {
      const endpoint = this.$partnerEndpoint("events/types");

      Api()
        .get(endpoint)
        .then(response => {
          this.eventTypes = response.data.data;
          if (this.eventTypes.length > 0) {
            this.eventtypeFilter = this.eventTypes[0];
          }
        });
    },

    getEventTimeframes() {
      const endpoint = this.$partnerEndpoint("events/timeframes");

      Api()
        .get(endpoint)
        .then(response => {
          this.eventTimeframes = response.data.data;
          if (this.eventTimeframes.length > 0) {
            this.timeframeFilter = this.eventTimeframes[0];
          }
        });
    },

    deleteEvent(eventId) {
      if (this.loading || this.updating || eventId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The event will be deleted.",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "events/" + this.selectedEventId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "Events",
                  query: { event_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The event was deleted.", "success");
                    this.getEvents(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete events.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  watch: {
    typeFilter() {
      this.getEvents(() => {
        //
      });
    },

    timeframeFilter() {
      this.getEvents(() => {
        //
      });
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}
</style>
